"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSession } from "next-auth/react";
import { UrlLinks } from "@/shared/config";

type UserProfile = {
  name?: string;
  email?: string;
  role?: string[];
  orgId?: string;
  orgName?: string;
  orgType?: string;
  permissions?: string[];
};

type ProfileContextValue = {
  profile: UserProfile | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);
const STORAGE_KEY = "schmgt.userprofile";

function mergeProfile(base: UserProfile | null, next: UserProfile | null) {
  return {
    ...(base ?? {}),
    ...(Object.fromEntries(
      Object.entries(next ?? {}).filter(([, value]) => value !== undefined)
    ) as UserProfile),
  };
}

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const sessionProfile = useMemo<UserProfile>(() => {
    if (!session?.user) {
      return {
        name: undefined,
        email: undefined,
        role: undefined,
        orgId: undefined,
        orgName: undefined,
        orgType: undefined,
        permissions: undefined,
      };
    }
    const user = session.user as
      | (typeof session.user & {
          role?: string[];
          orgId?: string;
          orgName?: string;
          orgType?: string;
          permissions?: string[];
        });
    const sessionName =
      session.user.name && session.user.name !== session.user.email
        ? session.user.name
        : undefined;

    return {
      name: sessionName,
      email: session.user.email ?? undefined,
      role: user?.role,
      orgId: user?.orgId,
      orgName: user?.orgName,
      orgType: user?.orgType,
      permissions: user?.permissions,
    };
  }, [session]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cached = window.localStorage.getItem(STORAGE_KEY);
    if (!cached) return;

    try {
      const parsed = JSON.parse(cached) as UserProfile;
      setProfile((current) => mergeProfile(current, parsed));
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasSessionProfile = Boolean(
      sessionProfile.name ||
        sessionProfile.email ||
        sessionProfile.role?.length ||
        sessionProfile.orgId ||
        sessionProfile.orgName ||
        sessionProfile.orgType ||
        sessionProfile.permissions?.length
    );

    if (!hasSessionProfile) return;

    setProfile((current) => {
      const merged = mergeProfile(current, sessionProfile);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, [sessionProfile]);

  const refresh = useCallback(async () => {
    if (status !== "authenticated") return;

    setIsLoading(true);
    try {
      const response = await fetch(UrlLinks.api.userProfile);
      if (!response.ok) return;

      const data = (await response.json()) as UserProfile;
      setProfile((current) => {
        const merged = mergeProfile(current, data);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        return merged;
      });
    } finally {
      setIsLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (status !== "authenticated") {
      hasFetchedRef.current = false;
      setProfile(null);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(STORAGE_KEY);
      }
      return;
    }

    const missingSessionProfile = !(
      sessionProfile.name && sessionProfile.role?.length && sessionProfile.orgId
    );

    if (!missingSessionProfile) {
      hasFetchedRef.current = true;
      return;
    }

    const cachedReady = Boolean(
      profile?.name && profile?.role?.length && profile?.orgId
    );

    if (cachedReady || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    void refresh();
  }, [profile, refresh, sessionProfile, status]);

  const value = useMemo(
    () => ({ profile, isLoading, refresh }),
    [profile, isLoading, refresh]
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }
  return context;
}
